import CryptoJS from 'crypto-js';
import diff_match_patch from 'diff-match-patch';

/**
 * Text Versioning Utility
 * Implements Git-like versioning with snapshots and patches using diff-match-patch
 */

export interface ContentSnapshot {
  dayKey: string;
  baseContent: string;
  baseHash: string;
  createdAt: string;
}

export interface ContentDiff {
  seq: number;
  patch: string;
  patchHash: string;
  contentHashAfter: string;
  isAutomatic: boolean;
  authorId: string;
  ts: string;
  size: number;
}

/**
 * Generate SHA-256 hash of text content
 */
export function hashContent(content: string): string {
  return CryptoJS.SHA256(content).toString();
}

/**
 * Get current day key in YYYY-MM-DD format (UTC)
 */
export function getCurrentDayKey(): string {
  const now = new Date();
  return now.toISOString().split('T')[0];
}

/**
 * Generate a patch from old content to new content
 */
export function generatePatch(oldContent: string, newContent: string): string | null {
  const dmp = new diff_match_patch();
  const patches = dmp.patch_make(oldContent, newContent);
  const patchText = dmp.patch_toText(patches);

  // Return null if no changes
  return patchText || null;
}

/**
 * Apply a patch to content
 * Returns [patchedContent, success]
 */
export function applyPatch(content: string, patchText: string): [string, boolean] {
  const dmp = new diff_match_patch();
  const patches = dmp.patch_fromText(patchText);
  const [patchedContent, results] = dmp.patch_apply(patches, content);

  // Check if all patches applied successfully
  const success = !results.includes(false);

  return [patchedContent, success];
}

/**
 * Reconstruct text from snapshot + diffs
 */
export function reconstructText(
  snapshot: ContentSnapshot,
  diffs: ContentDiff[]
): { content: string; success: boolean; failedAt?: number } {
  let workingContent = snapshot.baseContent;

  // Verify snapshot hash
  if (hashContent(workingContent) !== snapshot.baseHash) {
    return { content: workingContent, success: false, failedAt: -1 };
  }

  // Apply diffs in sequence
  for (const diff of diffs) {
    const [patchedContent, success] = applyPatch(workingContent, diff.patch);

    if (!success) {
      return { content: workingContent, success: false, failedAt: diff.seq };
    }

    // Verify hash after applying patch
    const actualHash = hashContent(patchedContent);
    if (actualHash !== diff.contentHashAfter) {
      return { content: workingContent, success: false, failedAt: diff.seq };
    }

    workingContent = patchedContent;
  }

  return { content: workingContent, success: true };
}

/**
 * Check if a patch is too large (threshold: 50KB)
 */
export function isLargePatch(patchText: string): boolean {
  return patchText.length > 51200; // 50KB
}

/**
 * Create a snapshot from content
 */
export function createSnapshot(content: string, dayKey: string): Omit<ContentSnapshot, 'createdAt'> {
  return {
    dayKey,
    baseContent: content,
    baseHash: hashContent(content),
  };
}

/**
 * Create a diff entry
 */
export function createDiff(
  seq: number,
  patchText: string,
  contentAfter: string,
  isAutomatic: boolean,
  authorId: string
): Omit<ContentDiff, 'ts'> {
  return {
    seq,
    patch: patchText,
    patchHash: hashContent(patchText),
    contentHashAfter: hashContent(contentAfter),
    isAutomatic,
    authorId,
    size: patchText.length,
  };
}
